import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { 
  Plus, 
  Search, 
  Filter, 
  Eye,
  Download,
  RefreshCw,
  Archive,
  ArchiveRestore,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  FileText,
  TrendingUp,
  TrendingDown
} from 'lucide-react';
import { getBatches, downloadBatchReport, processBatch, archiveBatch, unarchiveBatch } from '../../config/api';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';

const Batches = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [filterDateRange, setFilterDateRange] = useState('ALL');
  const [filterArchived, setFilterArchived] = useState(''); // '' = active, 'true' = archived
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [showFilters, setShowFilters] = useState(false);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch batches
  const { data: batchesData, isLoading, error, refetch } = useQuery({
    queryKey: ['batches', debouncedSearchTerm, filterStatus, filterDateRange, filterArchived, currentPage, sortField, sortDirection],
    queryFn: () => getBatches({
      search: debouncedSearchTerm,
      status: filterStatus !== 'ALL' ? filterStatus : undefined,
      dateRange: filterDateRange !== 'ALL' ? filterDateRange : undefined,
      archived: filterArchived || undefined,
      page: currentPage,
      limit: itemsPerPage,
      sortBy: sortField,
      sortOrder: sortDirection
    }),
    enabled: !!user,
    retry: 3
  });

  const batches = batchesData?.data?.batches || [];
  const pagination = batchesData?.data?.pagination || { total: 0, pages: 0, page: 1 };

  // Download a batch's CSV report.
  const handleDownloadBatch = async (batch) => {
    try {
      const blob = await downloadBatchReport(batch.id);
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `batch-${batch.batchNumber || batch.id}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to download batch');
    }
  };

  // Advance a batch through validation.
  const handleProcessBatch = async (batch) => {
    if (!window.confirm(`Run validation on batch ${batch.batchNumber || batch.id}?`)) return;
    try {
      const res = await processBatch(batch.id, 'VALIDATE');
      const b = res?.data || res;
      const errs = b?.errorCount || 0;
      toast.success(errs > 0 ? `Validated — ${errs} claim(s) need review` : 'Validated — batch ready to post');
      refetch();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to process batch');
    }
  };

  const handleArchiveBatch = async (batch) => {
    try {
      if (batch.isArchived) { await unarchiveBatch(batch.id); toast.success('Batch restored'); }
      else { await archiveBatch(batch.id); toast.success('Batch archived'); }
      refetch();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to archive batch');
    }
  };

  // Filtered and sorted batches
  const filteredBatches = useMemo(() => {
    let filtered = [...batches];

    // Apply search filter
    if (debouncedSearchTerm) {
      const searchLower = debouncedSearchTerm.toLowerCase();
      filtered = filtered.filter(batch => 
        batch.batchNumber?.toLowerCase().includes(searchLower) ||
        batch.file?.filename?.toLowerCase().includes(searchLower)
      );
    }

    // Apply status filter
    if (filterStatus !== 'ALL') {
      filtered = filtered.filter(batch => batch.status === filterStatus);
    }

    // Apply date range filter
    if (filterDateRange !== 'ALL') {
      const now = new Date();
      
      switch (filterDateRange) {
        case 'TODAY':
          filtered = filtered.filter(batch => {
            const batchDate = new Date(batch.createdAt);
            return batchDate.toDateString() === now.toDateString();
          });
          break;
        case 'THIS_WEEK':
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          filtered = filtered.filter(batch => {
            const batchDate = new Date(batch.createdAt);
            return batchDate >= weekAgo;
          });
          break;
        case 'THIS_MONTH':
          const monthAgo = new Date(now.getFullYear(), now.getMonth(), 1);
          filtered = filtered.filter(batch => {
            const batchDate = new Date(batch.createdAt);
            return batchDate >= monthAgo;
          });
          break;
        case 'THIS_QUARTER':
          const quarterStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
          filtered = filtered.filter(batch => {
            const batchDate = new Date(batch.createdAt);
            return batchDate >= quarterStart;
          });
          break;
        case 'THIS_YEAR':
          const yearStart = new Date(now.getFullYear(), 0, 1);
          filtered = filtered.filter(batch => {
            const batchDate = new Date(batch.createdAt);
            return batchDate >= yearStart;
          });
          break;
      }
    }

    return filtered;
  }, [batches, debouncedSearchTerm, filterStatus, filterDateRange]);

  // Handle sorting
  const handleSort = useCallback((field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  }, [sortField, sortDirection]);

  // Handle page change
  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  // Get status info
  const getStatusInfo = useCallback((status) => {
    const statusConfig = {
      CREATED: { color: 'bg-blue-100 text-blue-800', icon: Clock, label: 'Created' },
      VALIDATING: { color: 'bg-yellow-100 text-yellow-800', icon: RefreshCw, label: 'Validating' },
      NEEDS_REVIEW: { color: 'bg-orange-100 text-orange-800', icon: AlertCircle, label: 'Needs Review' },
      READY_TO_POST: { color: 'bg-purple-100 text-purple-800', icon: CheckCircle, label: 'Ready to Post' },
      POSTED: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Posted' },
      FAILED: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Failed' }
    };
    
    return statusConfig[status] || { color: 'bg-gray-100 text-gray-800', icon: Clock, label: status };
  }, []);

  // Format currency
  const formatCurrency = useCallback((amount) => {
    if (!amount) return '$0.00';
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(numAmount);
  }, []);

  // Format date
  const formatDate = useCallback((date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-red-400 mb-4" />
          <h2 className="text-lg font-semibold text-red-800 mb-2">Error Loading Batches</h2>
          <p className="text-red-600 mb-4">{error.message || 'Failed to load batch data'}</p>
          <button
            onClick={() => refetch()}
            title="Try again"
            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Batches</h1>
          <p className="text-gray-600">Manage chargeback processing batches</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => refetch()}
            disabled={isLoading}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw size={20} className={isLoading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={() => navigate('/app/upload')}
            title="Upload file"
            className="bg-primary-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-primary-700"
          >
            <Plus size={20} />
            Upload File
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 flex-1">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search by batch number or filename..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                title="Toggle filters"
                className={`px-3 py-2 border rounded-lg flex items-center gap-2 ${
                  showFilters 
                    ? 'border-primary-500 text-primary-600 bg-primary-50' 
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Filter size={16} />
                Filters
              </button>
              {/* Active / Archived toggle */}
              <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5 text-sm">
                <button onClick={() => { setFilterArchived(''); setCurrentPage(1); }}
                  className={`rounded-md px-3 py-1.5 font-medium ${filterArchived !== 'true' ? 'bg-white text-primary-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                  Active
                </button>
                <button onClick={() => { setFilterArchived('true'); setCurrentPage(1); }}
                  className={`rounded-md px-3 py-1.5 font-medium ${filterArchived === 'true' ? 'bg-white text-primary-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                  Archived
                </button>
              </div>
            </div>
            <div className="text-sm text-gray-500">
              {pagination.total} {filterArchived === 'true' ? 'archived' : 'total'} batches
            </div>
          </div>
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <div className="p-4 border-t border-gray-200 bg-gray-50">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="ALL">All Statuses</option>
                  <option value="CREATED">Created</option>
                  <option value="VALIDATING">Validating</option>
                  <option value="NEEDS_REVIEW">Needs Review</option>
                  <option value="READY_TO_POST">Ready to Post</option>
                  <option value="POSTED">Posted</option>
                  <option value="FAILED">Failed</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
                <select
                  value={filterDateRange}
                  onChange={(e) => setFilterDateRange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="ALL">All Dates</option>
                  <option value="TODAY">Today</option>
                  <option value="THIS_WEEK">This Week</option>
                  <option value="THIS_MONTH">This Month</option>
                  <option value="THIS_QUARTER">This Quarter</option>
                  <option value="THIS_YEAR">This Year</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Batches Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('batchNumber')}
                >
                  <div className="flex items-center gap-1">
                    Batch Number
                    {sortField === 'batchNumber' && (
                      <span className="text-primary-600">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('file')}
                >
                  <div className="flex items-center gap-1">
                    File
                    {sortField === 'file' && (
                      <span className="text-primary-600">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center gap-1">
                    Status
                    {sortField === 'status' && (
                      <span className="text-primary-600">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('recordCount')}
                >
                  <div className="flex items-center gap-1">
                    Records
                    {sortField === 'recordCount' && (
                      <span className="text-primary-600">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('totalAmount')}
                >
                  <div className="flex items-center gap-1">
                    Total Amount
                    {sortField === 'totalAmount' && (
                      <span className="text-primary-600">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('createdAt')}
                >
                  <div className="flex items-center gap-1">
                    Created
                    {sortField === 'createdAt' && (
                      <span className="text-primary-600">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center">
                    <div className="flex items-center justify-center">
                      <RefreshCw className="animate-spin h-8 w-8 text-primary-600 mr-3" />
                      <span className="text-gray-600">Loading batches...</span>
                    </div>
                  </td>
                </tr>
              ) : filteredBatches.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center">
                    <div className="text-gray-500">
                      <p className="text-lg font-medium mb-2">No batches found</p>
                      <p>
                        {searchTerm || filterStatus !== 'ALL' || filterDateRange !== 'ALL'
                          ? 'Try adjusting your search or filters'
                          : 'No batches available in the system'
                        }
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredBatches.map((batch) => {
                  const statusInfo = getStatusInfo(batch.status);
                  const StatusIcon = statusInfo.icon;
                  
                  return (
                    <tr key={batch.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {batch.batchNumber}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {batch.file?.filename || 'N/A'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {batch.file?.fileType || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {statusInfo.label}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {batch.recordCount || 0}
                        </div>
                        {batch.errorCount > 0 && (
                          <div className="text-sm text-red-600">
                            {batch.errorCount} errors
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {formatCurrency(batch.totalAmount)}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {formatDate(batch.createdAt)}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => navigate(`/app/batches/${batch.id}`)}
                            className="text-primary-600 hover:text-primary-900 p-1"
                            title="View Details"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            onClick={() => handleDownloadBatch(batch)}
                            className="text-green-600 hover:text-green-900 p-1"
                            title="Download"
                          >
                            <Download size={16} />
                          </button>
                          <button
                            onClick={() => handleProcessBatch(batch)}
                            className="text-blue-600 hover:text-blue-900 p-1"
                            title="Process (validate claims)"
                          >
                            <RefreshCw size={16} />
                          </button>
                          <button
                            onClick={() => handleArchiveBatch(batch)}
                            className={`p-1 ${batch.isArchived ? 'text-emerald-600 hover:text-emerald-800' : 'text-gray-400 hover:text-gray-700'}`}
                            title={batch.isArchived ? 'Restore batch' : 'Archive batch'}
                          >
                            {batch.isArchived ? <ArchiveRestore size={16} /> : <Archive size={16} />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="mt-6 flex justify-center">
          <nav className="flex items-center space-x-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              title="Previous page"
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            
            {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
              const page = Math.max(1, Math.min(pagination.pages, currentPage - 2 + i));
              return (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  title={`Go to page ${page}`}
                  className={`px-3 py-2 text-sm font-medium rounded-md ${
                    page === currentPage
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {page}
                </button>
              );
            })}
            
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === pagination.pages}
              title="Next page"
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </nav>
        </div>
      )}
    </div>
  );
};

export default Batches;
